import React, { useEffect, useId, useState } from 'react';
import { PrintRichText } from './PrintRichText';
import { usePrintInlineEdit } from './PrintInlineEditContext';

type EditableTag = 'span' | 'p' | 'h3' | 'div' | 'li';

interface PrintEditableTextProps {
  value: string;
  onCommit: (next: string) => void;
  className?: string;
  editClassName?: string;
  as?: EditableTag;
  multiline?: boolean;
  placeholder?: string;
  rich?: boolean;
  'aria-label'?: string;
}

/**
 * Texto de la hoja: en modo edición del bloque se vuelve input/textarea
 * in-place (misma tipografía), sin formulario aparte.
 */
export function PrintEditableText({
  value,
  onCommit,
  className,
  editClassName,
  as: Tag = 'span',
  multiline = false,
  placeholder = 'Escribí aquí…',
  rich = false,
  'aria-label': ariaLabel,
}: PrintEditableTextProps) {
  const ctx = usePrintInlineEdit();
  const isEditing = Boolean(ctx?.isEditing);
  const [local, setLocal] = useState(value);
  const fieldId = useId();

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const commit = () => {
    const next = local;
    if (next !== value) onCommit(next);
  };

  if (!isEditing) {
    if (rich) {
      return (
        <Tag className={className}>
          <PrintRichText text={value} />
        </Tag>
      );
    }
    return <Tag className={className}>{value}</Tag>;
  }

  const controlClass = [
    'print-editable',
    multiline ? 'print-editable--multiline' : 'print-editable--line',
    editClassName,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  if (multiline) {
    const rows = Math.min(16, Math.max(3, local.split('\n').length + 1));
    return (
      <textarea
        id={fieldId}
        className={controlClass}
        value={local}
        rows={rows}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onChange={(e) => setLocal(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setLocal(value);
            (e.target as HTMLTextAreaElement).blur();
          }
        }}
      />
    );
  }

  return (
    <input
      id={fieldId}
      type="text"
      className={controlClass}
      value={local}
      placeholder={placeholder}
      aria-label={ariaLabel}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          (e.target as HTMLInputElement).blur();
        }
        if (e.key === 'Escape') {
          setLocal(value);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

/** Título del bloque — módulo dedicado para evitar fallos de HMR. */
export function PrintEditableTitle({
  value,
  isTeaching,
  isPractice,
}: {
  value: string;
  isTeaching?: boolean;
  isPractice?: boolean;
}) {
  const edit = usePrintInlineEdit();
  const className = `print-block__title${isTeaching ? ' print-block__title--teaching' : ''}${
    isPractice ? ' print-block__title--practice' : ''
  }`;

  return (
    <PrintEditableText
      value={value}
      as="h3"
      className={className}
      aria-label="Título del bloque"
      onCommit={(next) => edit?.setLabel(next)}
    />
  );
}
